import { Aaro } from 'aaro';
import { config } from 'Constants';

// Utility to create a new Aaro instance
const createAaroInstance = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return new Aaro({
            baseUrl: config.url.AARO_BASE,
            accessToken: user.aaroToken || '',
        });
    } catch (error) {
        console.error('Aaro instance oluşturma hatası:', error);
        throw new Error('Aaro bağlantısı kurulamadı');
    }
};

// Belirtilen süre kadar beklemek için delay fonksiyonu
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// API çağrısı yapan ve rate limiting uygulayan fonksiyon
// Hata durumunda belirli sayıda yeniden deneme yapar
const callApi = async (endpoint, params, options = {}) => {
    const { signal, retryCount = 3, retryDelay = 500, minDelay = 300 } = options;

    const aaro = createAaroInstance();
    let lastError = null;

    for (let attempt = 0; attempt <= retryCount; attempt++) {
        if (signal?.aborted) {
            const abortError = new Error('İstek iptal edildi');
            abortError.name = 'AbortError';
            throw abortError;
        }

        try {
            // Her deneme öncesi bekleme (ilk deneme hariç)
            if (attempt > 0) {
                const waitTime = retryDelay * Math.pow(1.5, attempt - 1); // Exponential backoff
                console.log(`${endpoint} için ${attempt}. deneme, ${waitTime}ms bekleniyor...`);
                await delay(waitTime);
            }

            const response = await aaro.get(endpoint, params, { signal });

            // API'yi çok hızlı çağırmamak için minimum gecikme
            await delay(minDelay);

            return response.data;
        } catch (error) {
            lastError = error;

            // AbortError durumunda hemen sonlandır
            if (error.name === 'AbortError') {
                throw error;
            }

            // 409 (conflict) veya 429 (too many requests) hatalarında yeniden dene
            // Diğer hata tipleri için de yeniden deneme yapabiliriz
            const shouldRetry =
                attempt < retryCount &&
                (error.status === 409 ||
                    error.status === 429 ||
                    error.message?.includes('conflict') ||
                    error.message?.includes('rate limit') ||
                    !error.status); // Ağ hatası durumunda da dene

            if (!shouldRetry) {
                console.error(`${endpoint} API çağrısı başarısız oldu, yeniden deneme yapılmıyor:`, error);
                throw error;
            }

            console.warn(
                `${endpoint} API çağrısı başarısız oldu, yeniden deneniyor (${attempt + 1}/${retryCount}):`,
                error.message
            );
        }
    }

    throw lastError;
};

// Ana API fonksiyonları
export const getUrIsEmriPlan = async (data, options = {}) => {
    try {
        return await callApi('UrIsEmriPlan', data, options);
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('UrIsEmriPlan çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};

// Stok hareketlerini sayfalar halinde çeken fonksiyon
export const getSipStokHareketleri = async (dekontID, pageOptions = {}) => {
    const { page = 1, pageSize = 100, signal } = pageOptions;

    try {
        return await callApi(
            'SipStokHareketleri',
            {
                DekontID: dekontID,
                Sayfa: page,
                SayfaSatirSayisi: pageSize,
            },
            { signal, retryCount: 2 }
        );
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('SipStokHareketleri çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};

// Tüm stok hareketlerini çeken fonksiyon
export const getAllSipStokHareketleri = async (dekontID, options = {}) => {
    const { onProgress, delayMs = 300, maxPages = 100, signal } = options;
    let page = 1;
    let hasNext = true;
    const allRecords = [];

    try {
        // Her sayfa çağrısı arasında yeterli bekleme süresi garantisi
        const rateLimiter = async () => {
            await delay(delayMs);
            if (signal?.aborted) {
                const abortError = new Error('İşlem iptal edildi');
                abortError.name = 'AbortError';
                throw abortError;
            }
        };

        while (hasNext && page <= maxPages && !signal?.aborted) {
            onProgress?.(`SipStokHareketleri API - Sayfa: ${page}`);

            // Sayfa verilerini çek
            const data = await getSipStokHareketleri(dekontID, {
                page,
                pageSize: 100,
                signal,
            });

            if (data?.Model && Array.isArray(data.Model)) {
                allRecords.push(...data.Model);
            }

            hasNext = data?.SayfalandirmaBilgisi?.SonrakiSayfaVarMi || false;
            page++;

            // Bir sonraki sayfaya geçmeden önce rate limiting uygula
            if (hasNext && !signal?.aborted) {
                await rateLimiter();
            }
        }

        if (page > maxPages) {
            onProgress?.(`Uyarı: Maksimum sayfa sayısına (${maxPages}) ulaşıldı. Tüm veriler alınmamış olabilir.`);
        }

        return allRecords;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('AllSipStokHareketleri işlemi iptal edildi');
            return allRecords; // İptal edildiğinde o ana kadar toplanan kayıtları döndür
        }
        throw error;
    }
};

// İş emri detaylarını çeken fonksiyon
export const getUrIsEmri = async (isEmriID, options = {}) => {
    try {
        return await callApi(
            'UrIsEmri',
            {
                IsEmriID: isEmriID,
            },
            options
        );
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('UrIsEmri çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};

// İş emrinin operasyonlarını çeken fonksiyon
export const getUrAkisOperasyonMamul = async (isEmriID, options = {}) => {
    try {
        return await callApi(
            'UrAkisOperasyonMamul',
            {
                IsEmriID: isEmriID,
            },
            options
        );
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('UrAkisOperasyonMamul çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};

// İş emri planlarını çeken fonksiyon
export const getTumIsEmriPlan = async (isEmriID, options = {}) => {
    try {
        return await callApi(
            'UrIsEmriPlan/TumIsEmriPlan',
            {
                IsEmriID: isEmriID,
            },
            options
        );
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('TumIsEmriPlan çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};

// Alt iş emirlerini çeken fonksiyon
export const getAltIsEmri = async (isEmriID, options = {}) => {
    try {
        return await callApi(
            'UrIsEmri/AltIsEmri',
            {
                IsEmriID: isEmriID,
            },
            options
        );
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('AltIsEmri çağrısı iptal edildi');
            return null;
        }
        throw error;
    }
};
